
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, AlertCircle, CheckCircle2, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { setRecoveryFlag, clearRecoveryFlag } from '@/lib/recovery';

interface UrlError {
  type: string;
  code: string;
  description: string;
}

export const ResetPasswordPage = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [urlError, setUrlError] = useState<UrlError | null>(null);
  const [validTokens, setValidTokens] = useState(false);
  
  const navigate = useNavigate();

  // Parse tokens from URL hash or query params
  const parseFromUrl = () => {
    const hash = window.location.hash.substring(1);
    const search = window.location.search.substring(1);
    const params = new URLSearchParams(hash || search);
    
    return {
      accessToken: params.get('access_token'),
      refreshToken: params.get('refresh_token'),
      type: params.get('type'),
      error: params.get('error'),
      errorCode: params.get('error_code'),
      errorDescription: params.get('error_description')
    };
  };

  useEffect(() => {
    console.log('🔍 Reset password page loaded on domain:', window.location.hostname);
    console.log('🔍 Full URL:', window.location.href);

    const { accessToken, refreshToken, type, error, errorCode, errorDescription } = parseFromUrl();

    console.log('🔍 Reset tokens found:', {
      hasAccessToken: !!accessToken,
      hasRefreshToken: !!refreshToken,
      type,
      error,
      errorCode,
      errorDescription
    });

    // Check for errors first
    if (error) {
      console.log('❌ URL contains error:', { error, errorCode, errorDescription });
      setUrlError({
        type: error,
        code: errorCode || '',
        description: errorDescription || ''
      });
      return;
    }

    // Set the session with the tokens from the URL
    if (accessToken && refreshToken && type === 'recovery') {
      console.log('✅ Valid reset tokens found, setting session...');
      console.log('🔒 Setting recovery flag for security');
      
      // Set recovery flag BEFORE setting session for security
      setRecoveryFlag();
      
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken
      }).then(({ data, error }) => {
        if (error) {
          console.error('❌ Failed to set session:', error);
          setError('Failed to validate reset tokens. Please request a new reset email.');
        } else {
          console.log('✅ Session set successfully for password reset');
          setValidTokens(true);
        }
      });
    } else {
      console.log('❌ Missing or invalid reset tokens in URL');
      
      // Provide more specific error messages
      if (!accessToken && !refreshToken) {
        setError('This reset link is missing authentication tokens. The link may be incomplete or corrupted. Please request a new reset email.');
      } else if (type !== 'recovery') {
        setError(`This link is not a password recovery link (type: ${type}). Please use the correct password reset link.`);
      } else {
        setError('This reset link is missing required tokens. Please request a new reset email.');
      }
    }
  }, []);

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password || !confirmPassword) {
      setError('Please fill in both password fields');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }
    
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError('');

    try {
      console.log('🔄 Updating password...');
      
      const { error } = await supabase.auth.updateUser({
        password: password
      });

      if (error) {
        console.error('❌ Password update failed:', error);
        setError(error.message);
      } else {
        console.log('✅ Password updated successfully!');
        setSuccess(true);
        
        toast.success('Password updated successfully!', {
          description: 'You can now sign in with your new password'
        });
        
        // Clear the recovery flag and sign out completely
        console.log('🔒 Password updated successfully - clearing recovery flag and signing out');
        clearRecoveryFlag();
        await supabase.auth.signOut();
        
        // Redirect to login after a short delay
        setTimeout(() => {
          navigate('/auth');
        }, 2000);
      }
    } catch (err) {
      console.error('❌ Unexpected error:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Enhanced error message display
  if (urlError) {
    const getErrorMessage = () => {
      if (urlError.description) {
        const decoded = decodeURIComponent(urlError.description.replace(/\+/g, ' '));
        
        // Provide specific guidance for common errors
        if (decoded.includes('requested path is invalid')) {
          return `The redirect URL in this reset link is not properly configured in Supabase.\n\nThis usually means:\n• The URL is not absolute (missing https://)\n• The URL is not in Supabase's "Additional Redirect URLs" list\n• The token may have been used already\n\nError: ${decoded}`;
        }
        
        if (decoded.includes('Token has expired') || decoded.includes('expired')) {
          return `This reset link has expired. Reset links are only valid for 1 hour.\n\nPlease request a new password reset from https://iflsh.com/auth\n\nError: ${decoded}`;
        }
        
        return decoded;
      }
      return `This password reset link is invalid or has expired.\n\nCurrent domain: ${window.location.hostname}\nExpected domain: iflsh.com`;
    };

    const getErrorTitle = () => {
      if (urlError.description?.includes('requested path is invalid')) {
        return 'Invalid Redirect URL';
      }
      if (urlError.description?.includes('expired')) {
        return 'Link Expired';
      }
      
      switch (urlError.type) {
        case 'access_denied':
          return 'Access Denied';
        case 'invalid_request':
          return 'Invalid Request';
        default:
          return 'Reset Link Error';
      }
    };

    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-destructive/10 rounded-full">
              <AlertCircle className="w-6 h-6 text-destructive" />
            </div>
            <CardTitle className="text-xl text-center">{getErrorTitle()}</CardTitle>
            <CardDescription className="text-center">
              There was a problem with your password reset link
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription className="whitespace-pre-wrap">
                {getErrorMessage()}
              </AlertDescription>
            </Alert>
            
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground text-center">
                To resolve this issue:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1 pl-4">
                <li>• Go to the login page and request a new password reset</li>
                <li>• Make sure to request the reset from <strong>https://iflsh.com</strong></li>
                <li>• Check that the reset email isn't in your spam folder</li>
                <li>• Use the reset link within 1 hour of receiving it</li>
              </ul>
            </div>

            <div className="flex flex-col gap-2">
              <Button 
                onClick={() => navigate('/auth')} 
                className="w-full"
              >
                Go to Login Page
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.location.href = 'https://iflsh.com/auth'}
                className="w-full"
              >
                Visit https://iflsh.com/auth
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show loading state while validating tokens
  if (!validTokens && !error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Validating reset link...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Show success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-green-100 rounded-full">
              <CheckCircle2 className="w-6 h-6 text-green-600" />
            </div>
            <CardTitle className="text-xl text-center">Password Updated!</CardTitle>
            <CardDescription className="text-center">
              Your password has been successfully changed
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Alert>
              <CheckCircle2 className="h-4 w-4" />
              <AlertDescription>
                Redirecting you to the login page...
              </AlertDescription>
            </Alert>
            
            <Button 
              onClick={() => navigate('/auth')} 
              className="w-full"
            >
              Continue to Login
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main password reset form
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-primary/10 rounded-full">
            <Shield className="w-6 h-6 text-primary" />
          </div>
          <CardTitle className="text-xl text-center">Set New Password</CardTitle>
          <CardDescription className="text-center">
            Enter your new password below
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handlePasswordReset} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="password">New Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="Enter your new password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={loading}
                required
                minLength={6}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm New Password</Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder="Confirm your new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={loading}
                required
                minLength={6}
              />
            </div>
            
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            <Button type="submit" className="w-full" disabled={loading}>
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Update Password
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <Button
              variant="link"
              onClick={() => navigate('/auth')}
              disabled={loading}
            >
              Back to Login
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
